"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, Database, Settings, Rocket, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: "01",
  title: "Analyze",
  action: "Deep-Stack Infrastructure Discovery",
  deliverable: "Current State Inventory Report",
  icon: Search
}, {
  id: "02",
  title: "Identify",
  action: "Redundancy & Waste Detection",
  deliverable: "Inefficiency Heatmap",
  icon: Database
}, {
  id: "03",
  title: "Architect",
  action: "Cost-Optimized System Design",
  deliverable: "Savings Projection Blueprint",
  icon: Settings
}, {
  id: "04",
  title: "Implement",
  action: "Precision Migration & Tuning",
  deliverable: "Optimized Production Environment",
  icon: Rocket,
  isLast: true
}];
function StepItem({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className={`relative pl-12 md:pl-24 pb-16 last:pb-0 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`absolute left-[-9px] md:left-[7px] top-8 w-6 h-6 rounded-full bg-background border-4 z-10 transition-colors duration-500 ${isVisible ? "border-primary" : "border-muted"}`}>
        <div className="absolute inset-0 rounded-lg bg-primary/20 animate-ping opacity-50" />
      </div>

      {}
      <Card className="relative overflow-hidden transition-all duration-500 hover:scale-105 border border-border hover:border-primary/50 hover:ring-4 hover:ring-primary/10 hover:shadow-xl group bg-card/50 backdrop-blur-sm rounded-md">
        {}
        <div className="absolute inset-0 pointer-events-none opacity-[0.05] bg-[radial-gradient(circle_at_2px_2px,var(--foreground)_1px,transparent_0)] bg-[size:24px_24px]" />

        <CardHeader className="pb-4 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="font-mono text-sm border-primary/30 group-hover:border-primary group-hover:bg-primary/5 transition-colors px-3 py-1 rounded-md">
                PHASE {step.id}
              </Badge>
              <CardTitle className="font-default text-2xl tracking-tighter flex items-center gap-2 font-bold">
                {step.title}
              </CardTitle>
            </div>
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-300">
              <Icon className="w-5 h-5" />
            </div>
          </div>
        </CardHeader>

        <CardContent className="relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 pt-4 border-t border-border/50">
            {}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Settings className="w-4 h-4" />
                <h4 className="font-mono font-semibold text-xs uppercase tracking-widest">
                  Audit Action
                </h4>
              </div>
              <p className="text-foreground font-default font-medium leading-relaxed">
                {step.action}
              </p>
            </div>

            {}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CircleCheck className="w-4 h-4" />
                <h4 className="font-mono font-semibold text-xs uppercase tracking-widest">
                  Deliverable
                </h4>
              </div>
              <p className="text-foreground font-default font-medium leading-relaxed">
                {step.deliverable}
              </p>
            </div>
          </div>

          {}
          {step.isLast && <div className="mt-8 pt-6 border-t border-border/50 space-y-3">
              <div className="flex justify-between items-center text-sm font-medium">
                <span className="text-muted-foreground font-default">
                  Optimization Readiness
                </span>
                <span className="text-primary font-mono font-bold">100%</span>
              </div>
              <Progress value={100} className="h-2.5 rounded-md" />
            </div>}
        </CardContent>
      </Card>
    </div>;
}
export default function StepByStepMigrationProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <section data-section-id="240285" id="cost-optimization-audit" className="py-24 md:py-32 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 rounded-lg blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 rounded-md font-mono uppercase tracking-wider">
            Efficiency Protocol
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tighter mb-6 text-foreground">
            Architecting Savings,{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">
              Maximizing Performance
            </span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            Our systematic IT auditing framework identifies hidden redundancies and 
            architects a leaner, more resilient infrastructure to reduce your annual spend.
          </p>
        </div>

        {}
        <div className="max-w-4xl mx-auto relative">
          {}
          <div className="absolute left-0 md:left-4 top-8 bottom-8 w-1 bg-gradient-to-b from-primary/0 via-primary to-primary/0 rounded-md opacity-30" />

          {}
          <div className="relative">
            {steps.map((step, index) => <StepItem key={step.id} step={step} index={index} />)}
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-24 text-center">
          <a href="/ConnectPage" className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-default font-bold text-lg rounded-md transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/20">
            Request Your Free Audit
          </a>
        </div>
      </div>
    </section>;
}